/**
 * FHIR document view-model for ABDM M3 granted records.
 * Ports LIMS HealthDataDisplay contracts (exact reference resolution + Composition order)
 * without copying the large MUI component.
 */

export type FhirReference = {
  reference?: string;
  display?: string;
  type?: string;
};

/**
 * `bundleId` is the ABDM bundle UUID the attachment endpoint keys on — not the
 * bundle's position in `dataPushed.entries`.
 */
export type AttachmentRef = Record<string, unknown> & {
  fullUrl?: string;
  num?: number | string;
  bundleId?: string;
  sessionId?: string;
  refId?: string;
  title?: string;
};

export type TransformedBundle = Record<string, unknown> & {
  id?: string;
  bundleType?: string;
  careContextReference?: string;
  CompositionInfo?: Array<Record<string, unknown>>;
  AttachmentRefs?: Array<AttachmentRef>;
  PatientInfo?: Array<Record<string, unknown>>;
  PractitionerInfo?: Array<Record<string, unknown>>;
  OrganizationInfo?: Array<Record<string, unknown>>;
  EncounterInfo?: Array<Record<string, unknown>>;
  DocumentReferenceInfo?: Array<Record<string, unknown>>;
  DiagnosticReportInfo?: Array<Record<string, unknown>>;
  ObservationResult?: Array<Record<string, unknown>>;
  SpecimenInfo?: Array<Record<string, unknown>>;
  ImmunizationInfo?: Array<Record<string, unknown>>;
};

export type ConsentArtifactLike = {
  hipId?: string;
  hipName?: string;
  careContexts?: Array<{ careContextReference?: string; displayName?: string }>;
  dataPushed?: { entries?: TransformedBundle[] };
  _invalidEncryptionDataCareContexts?: string[];
  sessionStatus?: string;
};

export type ConsentSessionLike = {
  status?: string;
  sessionStatus?: string;
  consentArtifacts?: ConsentArtifactLike[];
  dataPushed?: { entries?: TransformedBundle[] };
  dataEraseAt?: string;
  identifiers?: {
    name?: string;
    abha_address?: string;
    abhaAddress?: string;
    abha_number?: string;
  };
};

export type CareContextProgress = {
  total: number;
  received: number;
  pending: number;
  invalid: number;
};

export const FHIR_BUNDLE_TYPE_TO_READABLE: Record<string, string> = {
  WellnessRecord: 'Wellness Record',
  PrescriptionRecord: 'Prescription Record',
  DischargeSummaryRecord: 'Discharge Summary Record',
  ImmunizationRecord: 'Immunization Record',
  OPConsultRecord: 'OP Consult Record',
  InvoiceRecord: 'Invoice Record',
  HealthDocumentRecord: 'Health Document Record',
  DiagnosticReportRecord: 'Diagnostic Report Record',
  Composition: 'Composition',
  DocumentReference: 'Document Reference',
  DiagnosticReport: 'Diagnostic Report',
  Bundle: 'Bundle',
};

export type FlattenedBundleEntry = {
  bundle: TransformedBundle;
  bundleIndex: number;
  hipId?: string;
  hipName?: string;
  careContextReference?: string;
  careContextDisplayName?: string;
};

export type ResolvedSectionEntry = {
  reference: string;
  resource: Record<string, unknown> | null;
  unresolved: boolean;
  processedType?: string;
};

export type DocumentSectionView = {
  title: string;
  entries: ResolvedSectionEntry[];
};

export type BundleDocumentView = {
  meta: FlattenedBundleEntry;
  compositionTitle?: string;
  sections: DocumentSectionView[];
  attachmentRefs: Array<AttachmentRef>;
};

const RESOURCE_ARRAY_SKIP = new Set(['careContexts']);

/** Statuses that must not expose clinical record content. */
export const BLOCKED_RECORD_STATUSES = new Set(['DENIED', 'REVOKED', 'EXPIRED']);

export function canExposeRecords(session: ConsentSessionLike | null | undefined): boolean {
  if (!session) return false;

  const statusFields = [session.status, session.sessionStatus]
    .filter(value => value != null && String(value).trim() !== '')
    .map(value => String(value).toUpperCase());

  // Block if either status field is denied / revoked / expired.
  if (statusFields.some(status => BLOCKED_RECORD_STATUSES.has(status))) {
    return false;
  }

  if (session.dataEraseAt) {
    const erase = new Date(session.dataEraseAt);
    if (!Number.isNaN(erase.getTime()) && erase.getTime() < Date.now()) {
      return false;
    }
  }

  // Only expose PHI when at least one status field is explicitly GRANTED.
  // Never authorize from artifact / dataPushed presence alone.
  return statusFields.includes('GRANTED');
}

/**
 * Flatten consentArtifacts[].dataPushed.entries (or legacy session.dataPushed.entries),
 * retaining source HIP and care-context metadata.
 */
export function flattenConsentBundles(session: ConsentSessionLike): FlattenedBundleEntry[] {
  const out: FlattenedBundleEntry[] = [];
  let bundleIndex = 0;

  const artifacts = Array.isArray(session.consentArtifacts) ? session.consentArtifacts : [];
  if (artifacts.length > 0) {
    for (const artifact of artifacts) {
      const entries = artifact.dataPushed?.entries || [];
      for (const bundle of entries) {
        const careContextReference = bundle.careContextReference;
        const careContextDisplayName = artifact.careContexts?.find(
          cc => cc.careContextReference === careContextReference
        )?.displayName;
        out.push({
          bundle,
          bundleIndex: bundleIndex++,
          hipId: artifact.hipId,
          hipName: artifact.hipName,
          careContextReference,
          careContextDisplayName,
        });
      }
    }
    return out;
  }

  const legacy = session.dataPushed?.entries || [];
  for (const bundle of legacy) {
    out.push({
      bundle,
      bundleIndex: bundleIndex++,
      careContextReference: bundle.careContextReference,
    });
  }
  return out;
}

function collectResources(bundle: TransformedBundle): Array<Record<string, unknown>> {
  const resources: Array<Record<string, unknown>> = [];
  for (const key of Object.keys(bundle)) {
    if (RESOURCE_ARRAY_SKIP.has(key)) continue;
    const value = bundle[key];
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (item && typeof item === 'object') {
        resources.push(item as Record<string, unknown>);
      }
    }
  }
  return resources;
}

function normalizeAlias(value: string): string {
  return value.replace(/^urn:uuid:/i, '').replace(/^#/, '').trim();
}

/**
 * Exact reference index: fullUrl, id, and safe normalized aliases only.
 * Does not fuzzy-join by resource type / display name.
 */
export function createExactReferenceIndex(bundle: TransformedBundle): Map<string, Record<string, unknown>> {
  const map = new Map<string, Record<string, unknown>>();

  const add = (key: unknown, resource: Record<string, unknown>) => {
    if (typeof key !== 'string' || !key.trim()) return;
    if (!map.has(key)) {
      map.set(key, resource);
    }
    const normalized = normalizeAlias(key);
    if (normalized && !map.has(normalized)) {
      map.set(normalized, resource);
    }
  };

  for (const resource of collectResources(bundle)) {
    add(resource.fullUrl, resource);
    add(resource.id, resource);
    if (typeof resource.fullUrl === 'string') {
      const parts = resource.fullUrl.split('/');
      const last = parts[parts.length - 1];
      add(last, resource);
      if (parts.length >= 2) {
        add(`${parts[parts.length - 2]}/${last}`, resource);
      }
    }
  }

  return map;
}

export function resolveExactReference(
  reference: FhirReference | string | null | undefined,
  index: Map<string, Record<string, unknown>>,
  bundle?: TransformedBundle
): Record<string, unknown> | null {
  if (reference == null) return null;
  const refString = typeof reference === 'string' ? reference : reference.reference;
  if (!refString || typeof refString !== 'string') return null;

  const direct = index.get(refString) || index.get(normalizeAlias(refString));
  if (direct) return direct;

  if (bundle && Array.isArray(bundle.AttachmentRefs)) {
    const attachment = bundle.AttachmentRefs.find(ref => ref?.fullUrl === refString);
    if (attachment) {
      return { ...attachment, processedType: 'AttachmentRef' };
    }
  }

  return null;
}

function resourceTitle(resource: Record<string, unknown>): string {
  const processedType = String(resource.processedType || resource.sourceResourceType || 'Resource');
  const label =
    (typeof resource.code === 'string' && resource.code) ||
    (typeof resource.name === 'string' && resource.name) ||
    (typeof resource.title === 'string' && resource.title) ||
    (typeof resource.display === 'string' && resource.display) ||
    processedType;
  return String(label);
}

/**
 * Resolve DiagnosticReport.result → Observation and DiagnosticReport.specimen → Specimen
 * using exact references only.
 */
export function resolveDiagnosticReportRelations(
  report: Record<string, unknown>,
  index: Map<string, Record<string, unknown>>
): { observations: Record<string, unknown>[]; specimens: Record<string, unknown>[]; unresolved: string[] } {
  const unresolved: string[] = [];
  const observations: Record<string, unknown>[] = [];
  const specimens: Record<string, unknown>[] = [];

  const resultRefs = Array.isArray(report.result) ? report.result : [];
  for (const ref of resultRefs) {
    const resolved = resolveExactReference(ref as FhirReference, index);
    if (resolved) observations.push(resolved);
    else unresolved.push(typeof ref === 'object' && ref && 'reference' in ref ? String((ref as FhirReference).reference) : 'result');
  }

  const specimenRefs = Array.isArray(report.specimen)
    ? report.specimen
    : report.specimen
      ? [report.specimen]
      : [];
  for (const ref of specimenRefs) {
    const resolved = resolveExactReference(ref as FhirReference, index);
    if (resolved) specimens.push(resolved);
    else unresolved.push(typeof ref === 'object' && ref && 'reference' in ref ? String((ref as FhirReference).reference) : 'specimen');
  }

  return { observations, specimens, unresolved };
}

/**
 * Walk Composition.section[] / section.entry[] in authored order into a display view-model.
 */
export function buildBundleDocumentView(entry: FlattenedBundleEntry): BundleDocumentView {
  const { bundle } = entry;
  const index = createExactReferenceIndex(bundle);
  const composition = Array.isArray(bundle.CompositionInfo) ? bundle.CompositionInfo[0] : null;
  const sectionsRaw = Array.isArray(composition?.section) ? (composition.section as Array<Record<string, unknown>>) : [];

  const sections: DocumentSectionView[] = sectionsRaw.map(section => {
    const title = typeof section.title === 'string' ? section.title : 'Section';
    const entriesRaw = Array.isArray(section.entry) ? (section.entry as FhirReference[]) : [];
    const entries: ResolvedSectionEntry[] = entriesRaw.map(ref => {
      const reference = ref?.reference || '';
      const resource = resolveExactReference(ref, index, bundle);
      return {
        reference,
        resource,
        unresolved: !resource,
        processedType: resource
          ? String(resource.processedType || resource.sourceResourceType || '')
          : undefined,
      };
    });
    return { title, entries };
  });

  // If no Composition sections, surface top-level DiagnosticReports in array order
  if (sections.length === 0 && Array.isArray(bundle.DiagnosticReportInfo)) {
    sections.push({
      title: 'Diagnostic Reports',
      entries: bundle.DiagnosticReportInfo.map((resource, i) => ({
        reference: String(resource.fullUrl || resource.id || `DiagnosticReport/${i}`),
        resource,
        unresolved: false,
        processedType: String(resource.processedType || 'DiagnosticReportInfo'),
      })),
    });
  }

  return {
    meta: entry,
    compositionTitle:
      (typeof composition?.title === 'string' && composition.title) ||
      (typeof composition?.type === 'string' && composition.type) ||
      undefined,
    sections,
    attachmentRefs: Array.isArray(bundle.AttachmentRefs) ? bundle.AttachmentRefs : [],
  };
}

export function buildSessionDocumentViews(session: ConsentSessionLike): BundleDocumentView[] {
  if (!canExposeRecords(session)) {
    return [];
  }
  return flattenConsentBundles(session).map(buildBundleDocumentView);
}

export function summarizeResourceCard(resource: Record<string, unknown>): {
  title: string;
  fields: Array<{ label: string; value: string }>;
} {
  const skip = new Set(['processedType', 'fullUrl', 'id', 'result', 'specimen', 'section', 'entry']);
  const fields: Array<{ label: string; value: string }> = [];
  for (const [key, value] of Object.entries(resource)) {
    if (skip.has(key)) continue;
    if (value == null || value === '') continue;
    if (typeof value === 'object') continue;
    fields.push({ label: key, value: String(value) });
  }
  return { title: resourceTitle(resource), fields: fields.slice(0, 12) };
}

export function computeCareContextProgress(session: ConsentSessionLike): CareContextProgress {
  const artifacts = Array.isArray(session.consentArtifacts) ? session.consentArtifacts : [];
  let total = 0;
  let received = 0;
  let pending = 0;
  let invalid = 0;

  for (const artifact of artifacts) {
    const careContexts = artifact.careContexts || [];
    total += careContexts.length;
    const processed = new Set(
      (artifact.dataPushed?.entries || [])
        .map(entry => entry.careContextReference)
        .filter((value): value is string => Boolean(value))
    );
    const invalidSet = new Set(artifact._invalidEncryptionDataCareContexts || []);
    for (const context of careContexts) {
      const ref = context.careContextReference;
      if (ref && processed.has(ref)) received += 1;
      else if (ref && invalidSet.has(ref)) invalid += 1;
      else pending += 1;
    }
  }

  if (total === 0) {
    const views = flattenConsentBundles(session).length;
    return { total: views, received: views, pending: 0, invalid: 0 };
  }

  return { total, received, pending, invalid };
}

function capitalize(value?: string | null): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function formatIndianDate(value?: string | null): string {
  if (!value) return '';
  // Already-friendly backend period strings like "21/06/2026 04:17 AM"
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatIndianDateTime(value?: string | null): string {
  if (!value) return '';
  if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function extractPeriodStart(period?: string | null): string | null {
  if (!period || typeof period !== 'string') return null;
  const fromMatch = period.match(/From:\s*(.+?)(?:\s*-\s*Until:|$)/i);
  if (fromMatch?.[1]) return fromMatch[1].trim();
  return period.trim() || null;
}

export function getBundleVisitCaption(bundle: TransformedBundle): string {
  const encounterPeriod =
    typeof bundle.EncounterInfo?.[0]?.period === 'string'
      ? String(bundle.EncounterInfo[0].period)
      : null;
  const visitStart = extractPeriodStart(encounterPeriod);
  if (visitStart) return `Visit date on ${visitStart}`;

  const diagnostic =
    bundle.DiagnosticReportInfo?.[0]?.effectiveDateTime ||
    bundle.DiagnosticReportInfo?.[0]?.issued;
  if (typeof diagnostic === 'string' && diagnostic) {
    return `Diagnostic report on ${formatIndianDateTime(diagnostic)}`;
  }

  const immunization = bundle.ImmunizationInfo?.[0]?.occurrence;
  if (typeof immunization === 'string' && immunization) {
    return `Immunization on ${formatIndianDateTime(immunization)}`;
  }

  const marked = bundle.CompositionInfo?.[0]?.date;
  if (typeof marked === 'string' && marked) {
    return `Date marked on received bundle: ${formatIndianDate(marked)}`;
  }

  return 'Date not available';
}

export function getBundleTypeLabel(bundle: TransformedBundle): string {
  const type = String(bundle.bundleType || '');
  return FHIR_BUNDLE_TYPE_TO_READABLE[type] || type || 'Record';
}

export function getCompositionSummary(
  entry: FlattenedBundleEntry
): {
  status?: string;
  dateMarked?: string;
  custodian?: string;
  visitDate?: string;
} {
  const { bundle } = entry;
  const composition = Array.isArray(bundle.CompositionInfo) ? bundle.CompositionInfo[0] : null;
  const index = createExactReferenceIndex(bundle);

  let custodian: string | undefined;
  if (composition?.custodian) {
    const resolved = resolveExactReference(composition.custodian as FhirReference, index, bundle);
    custodian =
      (typeof resolved?.name === 'string' && resolved.name) ||
      (typeof (composition.custodian as FhirReference).display === 'string'
        ? (composition.custodian as FhirReference).display
        : undefined);
  }

  const encounterPeriod =
    typeof bundle.EncounterInfo?.[0]?.period === 'string'
      ? String(bundle.EncounterInfo[0].period)
      : null;
  const visitDate = extractPeriodStart(encounterPeriod) || undefined;

  return {
    status: typeof composition?.status === 'string' ? capitalize(composition.status) : undefined,
    dateMarked:
      typeof composition?.date === 'string' ? formatIndianDate(composition.date) : undefined,
    custodian,
    visitDate,
  };
}

export function resolveCompositionLinkedResource(
  entry: FlattenedBundleEntry,
  kind: 'subject' | 'author' | 'encounter'
): Record<string, unknown> | null {
  const composition = Array.isArray(entry.bundle.CompositionInfo)
    ? entry.bundle.CompositionInfo[0]
    : null;
  if (!composition) return null;
  const index = createExactReferenceIndex(entry.bundle);

  if (kind === 'subject' && composition.subject) {
    return resolveExactReference(composition.subject as FhirReference, index, entry.bundle);
  }
  if (kind === 'author' && composition.author) {
    const authors = Array.isArray(composition.author) ? composition.author : [composition.author];
    for (const author of authors) {
      const resolved = resolveExactReference(author as FhirReference, index, entry.bundle);
      if (resolved) return resolved;
    }
  }
  if (kind === 'encounter' && composition.encounter) {
    return resolveExactReference(composition.encounter as FhirReference, index, entry.bundle);
  }
  return null;
}

export function getPatientInfoFields(
  patient: Record<string, unknown> | null,
  session: ConsentSessionLike
): Array<{ label: string; value: string }> {
  if (!patient) return [];
  const identifiers = session.identifiers || {};
  const fields: Array<{ label: string; value: string }> = [];
  if (patient.name) fields.push({ label: 'Name', value: String(patient.name) });
  if (patient.gender) fields.push({ label: 'Gender', value: capitalize(String(patient.gender)) });
  if (patient.birthDate) {
    fields.push({ label: 'Birth Date', value: formatIndianDate(String(patient.birthDate)) });
  }
  const abhaAddress = identifiers.abha_address || identifiers.abhaAddress;
  if (abhaAddress) fields.push({ label: 'ABHA Address', value: String(abhaAddress) });
  if (identifiers.abha_number) {
    fields.push({ label: 'ABHA Number', value: String(identifiers.abha_number) });
  }
  if (Array.isArray(patient.telecom) && patient.telecom.length) {
    fields.push({ label: 'Contact', value: patient.telecom.map(String).join(', ') });
  }
  if (Array.isArray(patient.identifier) && patient.identifier.length) {
    const mrn = patient.identifier.find(
      (item: Record<string, unknown>) =>
        String(item?.type || '')
          .toLowerCase()
          .includes('medical')
    ) as Record<string, unknown> | undefined;
    const value = mrn?.value || (patient.identifier[0] as Record<string, unknown>)?.value;
    if (value) fields.push({ label: 'Medical Record Number', value: String(value) });
  }
  return fields;
}

export function getPractitionerInfoFields(
  practitioner: Record<string, unknown> | null
): Array<{ label: string; value: string }> {
  if (!practitioner) return [];
  const fields: Array<{ label: string; value: string }> = [];
  if (practitioner.name) fields.push({ label: 'Name', value: String(practitioner.name) });
  if (Array.isArray(practitioner.identifier) && practitioner.identifier.length) {
    const id = practitioner.identifier[0] as Record<string, unknown>;
    const type = id?.type ? String(id.type) : 'Identifier';
    const value = id?.value ? String(id.value) : '';
    if (value) fields.push({ label: 'Identifier', value: `${type}: ${value}` });
  }
  return fields;
}

export function getEncounterInfoFields(
  encounter: Record<string, unknown> | null
): Array<{ label: string; value: string }> {
  if (!encounter) return [];
  const fields: Array<{ label: string; value: string }> = [];
  if (encounter.period) fields.push({ label: 'Period', value: String(encounter.period) });
  if (encounter.status) fields.push({ label: 'Status', value: capitalize(String(encounter.status)) });
  if (encounter.class) fields.push({ label: 'Class', value: capitalize(String(encounter.class)) });
  if (Array.isArray(encounter.identifier) && encounter.identifier.length) {
    const id = encounter.identifier[0] as Record<string, unknown>;
    if (id?.value) fields.push({ label: 'Identifier', value: String(id.value) });
  }
  if (encounter.subject && typeof encounter.subject === 'object') {
    const subject = encounter.subject as FhirReference;
    if (subject.display) fields.push({ label: 'Subject', value: String(subject.display) });
  }
  if (encounter.hospitalization && typeof encounter.hospitalization === 'object') {
    const hospitalization = encounter.hospitalization as Record<string, unknown>;
    if (hospitalization.dischargeDisposition) {
      fields.push({
        label: 'Hospitalization',
        value: `Discharge Disposition: ${String(hospitalization.dischargeDisposition)}`,
      });
    }
  }
  return fields;
}
